/** @module csi/table/_CopyPasteMixin */
define([
	"dojo/_base/declare",
	"dojo/_base/event",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/keys",
	"dojo/on",
	"dojo/query",
	"dojo/touch",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"lodash",
	"src/c8-platform/service/aggregateStoreService",
	"src/c8-platform/type/value",
	"csi",
	"csi/action",
	"csi/utility/coreUtility",
	"csi/heading/_base",
	"csi/html",
	"csi/service/writerService",
	"dojo/text!csi/templates/tableCopyPaste.html"
], function(declare, event, lang, aspect, domClass, keys, on, query, touch, _TemplatedMixin, _WidgetBase, _WidgetsInTemplateMixin, _,
	{cacheAggregateStoreDragFillIds, getCachedAggregateStoreDragFillId, getCachedAggregateStoreDragFillOperation, isAggregateStoreIdUnknown},
	{isContainerValueType}, csi, csiAction, {isDefined}, csiHeading, html, {writeCopyAggregateStoreValue}, template) {

	var ANCHOR_HALF_HEIGHT = 4.5;
	var CSS_COPY = "csi-table-view-copy";
	var CSS_PASTE = CSS_COPY + "-paste-box";
	var DRAG_TIMEOUT = 50;

	var _dragHandles;
	var _dragMouseY;

	/**
	 * Update mouse position
	 * @param {Event} e
	 */
	function _onDragMouse(e) {
		_dragMouseY = e.y;
	}

	/**
	 * Return delta to scroll based on distance from table body (all measured in pixels)
	 * @param {number} distanceFromBody
	 * @return {number}
	 */
	function _getDragDelta(distanceFromBody) {
		if (distanceFromBody <= 20) {
			return 8;
		} else if (distanceFromBody <= 50) {
			return 16;
		}

		return 25;
	}

	/**
	 * Return scrolling element that needs to scroll based on mouse y position during drag
	 * @param {HTMLElement} element 
	 * @return {HTMLElement}
	 */
	function getScrollingContainerElement(element) {
		while (element) {
			if (html.isScrollingVertically(element)) {
				const {bottom, top} = element.getBoundingClientRect();

				if ((_dragMouseY < top) || (_dragMouseY > bottom)) {
					return element;
				}
			}

			element = element.parentNode;
		}

		return html.pageContentScroll();
	}

	var _CopyWidget = declare([_WidgetBase, _TemplatedMixin, _WidgetsInTemplateMixin], {
		baseClass: CSS_COPY,
		templateString: template,

		// DOMNode: attach points
		copyAnchor: null,
		copyBox: null,
		pasteOutline: null,
		focusNode: null,

		/** @property {number[]} allowedIndices - if specified, allow copy paste to indices in array rather than single _copyIndex */
		allowedIndices: null,

		/** @property {DOMNode} _copyCell - cell being copied */
		_copyCell: null,

		/** @property {number} _copyIndex - column index of cell being copied */
		_copyIndex: null,

		/** @property {DOMNode} _pasteCell - endpoint of cells being pasted */
		_pasteCell: null,

		allowCopy: function(/*DOMNode*/td) {
			// summary: access check for column to determine if it makes sense to copy and paste
			return true;
		},

		allowPaste: function(/*DOMNode*/td) {
			// summary: access check for cell to determine if a value can be pasted into it
			return true;
		},

		onCopyPaste: function(/*DOMNode*/copyCell, /*NodeList*/pasteList) {
			// summary: copy value from copy cell to pasted cells
			pasteList.forEach(function(td) {td.innerHTML = this;}, copyCell.innerHTML);
		},

		destroy: function() {
			// summary: release handles
			this.inherited(arguments);

			if (this._moveHandle) {
				this._moveHandle.remove();
			}
		},

		startup: function() {
			// summary: listen to onmousemove on table when table is not disabled
			const view = this.getParent();
			this.inherited(arguments);
			view.watch("disabled", lang.hitch(this, this._watchDisabled));
			this._watchDisabled();
			this.own(
				on(this.copyAnchor, touch.press, this._onDown.bind(this)),
				on(this.copyBox, touch.move, this._onMove.bind(this)),
				on(this.pasteOutline, touch.move, this._onMove.bind(this)),

				// any action can possibly refresh the table cells, so need to reset to make sure copy cell still valid
				aspect.after(view, "postAction", this._reset.bind(this))
			);
		},

		_endDrag: function() {
			// summary: cleanup after drag
			const view = this.getParent();
			const headTable = view.getHeadTableNode();
			const bodyTable = view.getBodyTableNode();

			domClass.remove(headTable, CSS_COPY);
			domClass.remove(bodyTable, CSS_COPY);
			this._getPasteList().removeClass(CSS_PASTE);
			html.setVisible(this.domNode, false);

			_.each(_dragHandles, function(h) {
				h.remove();
			});

			_dragHandles = null;
			this._copyCell = this._copyIndex = this._pasteCell = null;
			this.allowedIndices = null;
		},

		_getCell: function(/*Event*/e) {
			// summary: get cell for current copy column
			var elem = e.target;

			// if dragging back over the paste outline (drag down then back up), then need to
			// hide paste outline to find the real table cell underneath
			if ((elem == this.pasteOutline) || (elem == this.copyBox)) {
				elem = this._getDomNodeAtPosition(e.clientX + 1, e.clientY + 1);
			}

			// determine if mouse is over cell that represents an attribute; ignore "td" cells that are in a rich text table within the cell
			var table = this.getParent();
			var td = table.getCell(elem);
			var tr = td ? table.getRefNode(td) : null;

			if (!tr || (tr !== td.parentNode) || !table.isRefNode(tr, true)) {
				return null;
			}

			var allowed = this.allowedIndices;
			var copyIndex = this._copyIndex;
			if (copyIndex == null) {
				// no copy cell yet, so check if column can be copied and if there are cells to paste to;
				// look up and down 10 cells for first paste cell - don't want to look too far in case
				// table has many rows
				if (!this.allowCopy(td)) {
					return null;
				}

				if (!this._getNextCell(td, false, 10) && !this._getNextCell(td, true, 10)) {
					// if copy/paste is allowed to other columns, then assume there are other cells to paste to
					// if there is more than one allowed column and the current cell can be pasted to;
					// in matrix there should hopefully not be rules where some are editable and some are not
					if (!allowed || (allowed.length === 1) || !this.allowPaste(td)) {
						return null;
					}
				}

				return td;
			}

			var index = table.getTableCellIndex(td);
			if (index == copyIndex) {
				// return cell in copy column
				return td;
			}

			// ensure index is within bounds of allowed indices
			if (allowed) {
				copyIndex = Math.min(Math.max(index, allowed.first()), allowed.last());
			}

			return table.getTableCellAtIndex(tr, copyIndex);
		},

		/**
		 * Return bounding rect of given node relative to table origin
		 * @param {DOMNode} td
		 * @return {DOMRect}
		 */
		_getCellRect: function(td) {
			const view = this.getParent();
			const cellRect = td.getBoundingClientRect();
			const contRect = view.tableContainerNode.getBoundingClientRect();

			return {
				height: cellRect.height,
				left: cellRect.left - contRect.left - 1, // -1 accounts for left table border
				top: cellRect.top - contRect.top,
				width: cellRect.width
			};
		},

		/**
		 * Return element at given position; hide this widget while doing so to get underlying table element
		 * @param {number} x
		 * @param {number} y
		 * @return {DOMNode}
		 */
		_getDomNodeAtPosition: function(x, y) {
			var rootNode = this.domNode;
			html.setVisible(rootNode, false);
			var domNode = document.elementFromPoint(x, y);
			html.setVisible(rootNode, true);
			return domNode;
		},

		_getNextCell: function(/*DOMNode*/td, /*Boolean*/bUp, /*Number?*/limit, /*Boolean*/bAnyCell) {
			// summary: return next column cell that can be pasted
			var table = this.getParent();
			var hl = table.getHierarchyLevel((this._copyCell || td).parentNode);
			var paste = bAnyCell ? Boolean : this.allowPaste;
			return table._getNextCopyPasteCell(hl, td, bUp, limit || 100, paste);
		},

		/**
		 * If have hit cell spanning multiple columns (group row), then find cell at previous paste index in direction of copy cell (up/down)
		 * @param {DOMNode} newPasteCell
		 * @param {boolean} [up] - if specified, then force direction of finding next valid cell; otherwise, base it on location of pasteCell vs copyCell
		 * @return {DOMNode}
		 */
		_getValidPasteCell: function(newPasteCell, up) {
			var table = this.getParent();
			var pasteRow = table.getRefNode(newPasteCell);
			if (!pasteRow) {
				return null;
			}

			var copyCell = this._copyCell;
			var pasteCell = this._pasteCell;
			var copyIndex = table.getTableCellIndex(copyCell);
			var pasteIndex = table.getTableCellIndex(pasteCell || copyCell);

			if (up === undefined) {
				up = (pasteRow.offsetTop < copyCell.offsetTop);
			}

			var sibling = up ? "nextSibling" : "previousSibling";

			do {
				pasteRow = pasteRow[sibling];

				if (!pasteRow) {
					return null;
				}

				pasteCell = table.getTableCellAtIndex(pasteRow, copyIndex);
				pasteIndex = pasteCell ? table.getTableCellIndex(pasteCell) : -1;
			} while (pasteIndex != copyIndex);

			return pasteCell;
		},

		/**
		 * Apply paste CSS class to all valid paste cells
		 * @param {boolean} bUp
		 */
		_markPasteCells: function(bUp) {
			var table = this.getParent();
			var copyCell = this._copyCell;
			var pasteCell = this._pasteCell;
			var copyIndex = table.getTableCellIndex(copyCell);
			var pasteIndex = table.getTableCellIndex(pasteCell);
			var startIndex = Math.min(copyIndex, pasteIndex);
			var endIndex = Math.max(copyIndex, pasteIndex);
			var copyRow = copyCell.parentNode;
			var pasteRow = pasteCell.parentNode;
			var lastCell;

			// iterate over valid columns
			for (var i = startIndex; i <= endIndex; ++i) {
				copyCell = table.getTableCellAtIndex(copyRow, i);
				pasteCell = table.getTableCellAtIndex(pasteRow, i);
				lastCell = this._getNextCell(pasteCell, bUp);

				// if not on original copy column, then need to test for access to paste to copyCell
				if ((i === copyIndex) || !this.allowPaste(copyCell)) {
					pasteCell = this._getNextCell(copyCell, bUp);
				} else {
					pasteCell = copyCell;
				}

				// iterate over rows
				while (pasteCell != lastCell) {
					domClass.add(pasteCell, CSS_PASTE);
					pasteCell = this._getNextCell(pasteCell, bUp);
				}
			}
		},

		_getPasteList: function() {
			// summary: return all paste cells
			return this.domNode ? query("." + CSS_PASTE, this.getParent().bodyNode) : [];
		},

		/**
		 * Initialize drag-fill styling and process
		 * @param {Event} e
		 */
		_onDown: function(e) {
			var table = this.getParent();

			_dragHandles = [on(document, touch.release, lang.hitch(this, this._onRelease))];

			if (table.scrollData.isScrolled() || html.isScrollingVertically(html.pageContentScroll())) {
				// start timer to handle table scrolling and track mouse position
				_dragHandles.push(on(document, touch.move, _onDragMouse));
				_dragMouseY = null;
				this._onDragTimeout();
			}
			// Override document keydown to catch key stroke even when widget does not have focus
			_dragHandles.push(on(document, "keydown", lang.hitch(this, this._onEscape)));
			// Override table keydown in addition to document keydown to prevent dialog from closing, when clicking ESC
			_dragHandles.push(on(table.domNode, "keydown", lang.hitch(this, this._onEscape)));
			this._copyIndex = table.getTableCellIndex(this._copyCell);

			domClass.add(table.getHeadTableNode(), CSS_COPY);
			domClass.add(table.getBodyTableNode(), CSS_COPY);
			html.setVisible(this.copyBox, true);

			event.stop(e);
		},

		_onEscape: function(/*Event*/e) {
			// summary: catch escape key to end drag
			if (e.keyCode == keys.ESCAPE) {
				this._endDrag();
				event.stop(e);
			}
		},

		/**
		 * Update scroll position of table based on location of mouse
		 */
		_onDragTimeout: function() {
			// if no longer dragging, then do nothing
			if (!_dragHandles) {
				return;
			}

			if (_dragMouseY != null) {
				const table = this.getParent();
				const {footNode, naturalScrollNode, scrollData, tableScrollNode: verticalScrollNode} = table;
				const bNaturalScroll = scrollData.useNaturalHorizontalScrolling();
				const tableScrollNode = bNaturalScroll ? naturalScrollNode : verticalScrollNode;
				const footRect = footNode.getBoundingClientRect();
				const scrollNode = getScrollingContainerElement(tableScrollNode);
				const rect = scrollNode.getBoundingClientRect();
				const bodyTop = rect.top;
				const bodyBottom = rect.bottom;
				const x = footRect.left + 10;

				let delta;
				let y;
				let up = false;

				if (_dragMouseY < bodyTop) {
					// if mouse above table, then scroll up
					delta = -_getDragDelta(bodyTop - _dragMouseY);
					y = bodyTop + 10;
					up = true;
				} else if (_dragMouseY > bodyBottom) {
					// if mouse below table, then scroll down
					delta = _getDragDelta(_dragMouseY - bodyBottom);
					// Naturally scrolled table might have horizontal scroll bar.
					// The entire view might have horizontal scroll bar, especially composite views
					// Allow space for it when calculating new "y"
					// this is important when calculate domNode below (this._getDomNodeAtPosition(x, y))
					// if "y" will point to scroll bar - entire table domNode will be returned instead of cell domNode (C8-34495)
					const bCompositeView = table.getParent()?.domNode?.classList.contains("htmlViewContent");
					const bTableScrolledH = (bNaturalScroll && html.isScrollingHorizontally(tableScrollNode)) ||
						(bCompositeView && html.isScrollingHorizontally(html.pageContentScroll()));
					const hScrollBarHeight = bTableScrolledH ? html.getHorizontalScrollbarHeight() : 0
					y = bodyBottom - 10 - hScrollBarHeight;
				}

				if (delta) {
					// find new paste cell
					var domNode = this._getDomNodeAtPosition(x, y);
					var pasteCell = this._pasteCell;

					var newPasteCell = domNode && this._getValidPasteCell(domNode, up);

					var bSameScroll = false;
					if (newPasteCell) {
						var oldScrollTop = scrollNode.scrollTop;
						var newScrollTop = oldScrollTop + delta;
						scrollNode.scrollTop = newScrollTop;
						bSameScroll = (scrollNode.scrollTop == oldScrollTop);

						// Fix for cell position wrong on absolute positioned scrollable fixed header tables
						// once table is scrolled for fixed header table the drag selection element may not follow the scrolled table as table is positioned absolute
						var copyRect = this._getCellRect(this._copyCell);
						html.setPixels(this.domNode, html.TOP, copyRect.top);
					}

					// Page Center is scrolling and we are already below the table
					if (!newPasteCell) {
						// Mark cell in last row. If no newPasteCell - just keep timeout running
						newPasteCell = this._getNextCell(pasteCell, up, null, true);
					} else if (bSameScroll) {
						// if already scrolled to top or bottom, then make sure to select first/last cell
						newPasteCell = this._getNextCell(newPasteCell, up, null, true);
					} else if (newPasteCell && pasteCell && (newPasteCell != pasteCell)) {
						var pasteCellAbove = (pasteCell.offsetTop < newPasteCell.offsetTop);
						var copyCellAbove = (this._copyCell.offsetTop < newPasteCell.offsetTop);

						if (up == pasteCellAbove) {
							// if going away from copy cell, then don't go backwards - in other words, if existing paste cell is above the new paste
							// cell when going up, then use existing paste cell (and vice-versa)
							newPasteCell = pasteCell;
						} else if (up == copyCellAbove) {
							// if going towards copy cell, then keep only row closest to scroll direction selected
							newPasteCell = this._getNextCell(newPasteCell, up, null, true);
						}
					}

					if (newPasteCell) {
						this._setPasteCell(newPasteCell);
					}
				}
			}

			// restart timer to see if we need to scroll again
			setTimeout(this._onDragTimeout.bind(this), DRAG_TIMEOUT);
		},

		/**
		 * End current drag-fill process if Escape key pressed; trap all other events
		 * @param {Event} e
		 */
		_onKeyDown: function(e) {
			if (_dragHandles && (e.keyCode == keys.ESCAPE)) {
				this._endDrag();
			}

			event.stop(e);
		},

		_onMove: function(/*Event*/e) {
			// summary: position copyAnchor over current cell
			var rootNode = this.domNode;
			var table = this.getParent();
			var td = this._getCell(e);

			if (_dragHandles != null) {
				// ensure that paste cell is on valid row - one that is not a group or action row
				if (td && table.cellSpansMultipleColumns(td)) {
					td = this._getValidPasteCell(e.target);
				}

				// update pasteOutline to include cell moved over
				this._setPasteCell(td);

				// update mouse position
				_onDragMouse(e);
				event.stop(e);
			} else if (!td) {
				// hide anchor
				this._reset();
			} else if (td != this._copyCell) {
				// display anchor on new cell
				var rect = this._getCellRect(td);

				// set copy box height
				html.setPixels(this.copyBox, html.HEIGHT, rect.height);

				// set top of anchor to height of cell minus half the height of the anchor
				html.setPixels(this.copyAnchor, html.TOP, rect.height - ANCHOR_HALF_HEIGHT);

				// set widget origin to top-left corner of cell along with width
				html.setPixels(rootNode, html.TOP, rect.top);
				html.setPixels(rootNode, html.LEFT, rect.left);
				html.setPixels(rootNode, html.WIDTH, rect.width);

				if (!this._copyCell) {
					html.setVisible(rootNode, true);
					html.setVisible(this.copyBox, false);
					html.setVisible(this.pasteOutline, false);
				}

				this._copyCell = td;
			}
		},

		/**
		 * Hide copy box until a valid cell is entered again and handled in _onMove
		 */
		_reset: function() {
			html.setVisible(this.domNode, false);
			this._copyCell = null;
		},

		_onRelease: function(/*Event*/e) {
			// summary: process paste action
			var nl = this._getPasteList();
			if (nl.length) {
				this.onCopyPaste(this._copyCell, nl);
			}

			this._endDrag();
			event.stop(e);
		},

		/**
		 * Set paste cell and highlight all those in between it and the copy cell
		 * @param {DOMNode} td
		 */
		_setPasteCell: function(td) {
			if (td && (td != this._pasteCell)) {
				var pasteOutline = this.pasteOutline;
				var bUp;

				this._pasteCell = td;

				if (td == this._copyCell) {
					// hide paste outline if back over original copy cell
					html.setVisible(pasteOutline, false);
				} else {
					// draw outline covering column from copy call to paste cell
					const mbCopy = this._copyCell.getBoundingClientRect();
					const mbPaste = td.getBoundingClientRect();
					let {height, width} = mbCopy;
					let top = 0;
					let left = 0;

					if (mbCopy.top < mbPaste.top) {
						// dragging down
						height = mbPaste.top + mbPaste.height - mbCopy.top;
					} else if (mbCopy.top > mbPaste.top) {
						// dragging up
						bUp = true;
						height = mbCopy.top + mbCopy.height - mbPaste.top;
						top = mbPaste.top - mbCopy.top;
					}

					if (mbCopy.left < mbPaste.left) {
						// dragging right
						width = mbPaste.left + mbPaste.width - mbCopy.left;
					} else if (mbCopy.left > mbPaste.left) {
						// dragging left
						width = mbCopy.left + mbCopy.width - mbPaste.left;
						left = mbPaste.left - mbCopy.left;
					}

					html.setPixels(pasteOutline, html.HEIGHT, height);
					html.setPixels(pasteOutline, html.WIDTH, width);
					html.setPixels(pasteOutline, html.LEFT, left);
					html.setPixels(pasteOutline, html.TOP, top);
					html.setVisible(pasteOutline, true);
				}

				// remove paste class from previous paste cells and add to new ones
				this._getPasteList().removeClass(CSS_PASTE);

				if (td != this._copyCell) {
					this._markPasteCells(bUp);
				}
			}
		},

		_watchDisabled: function(/*String*/name, /*Boolean*/oldValue, /*Boolean*/disabled) {
			// summary: add/remove onmousemove handler
			if (disabled && this._moveHandle) {
				this._moveHandle.remove();
				this._moveHandle = null;
				this._copyCell = null;
				html.setVisible(this.domNode, false);
			} else if (!disabled && !this._moveHandle) {
				this._moveHandle = on(this.getParent().bodyNode, touch.move, lang.hitch(this, this._onMove));
			}
		}
	});

	return declare(null, {
		/** @property {Object} _copyParams - parameters for current cell being copied */
		_copyParams: null,

		/** @property {_CopyWidget} _copyWidget - widget to handle copy & paste */
		_copyWidget: null,

		/**
		 * Extension point indicating a value has been changed in view using copy/paste in case there needs to be view-specific processing
		 * @param {Controller} ac
		 */
		onActionPaste: function(ac) { },

		postCreate: function() {
			// summary: add copy-paste widget unless table is read-only
			this.inherited(arguments);

			if (!this.getContext().isReadOnly()) {
				this._copyWidget = new _CopyWidget({
					allowCopy: lang.hitch(this, this._canCopy),
					allowPaste: lang.hitch(this, this._canPaste),
					onCopyPaste: lang.hitch(this, this._copyPaste)
				}).placeAt(this.tableContainerNode.firstChild, "after");
			}
		},

		/**
		 * Return urls of adjacent cells in either the up or down direction
		 * @param {HTMLElement} cell 
		 * @param {boolean} bUp 
		 * @return {cnl[]}
		 */
		_getAdjacentCopyPasteUrls(cell, bUp) {
			const hl = this.getHierarchyLevel(cell.parentNode);
			const urls = [];
			let valid = false;
			let params;

			for (let i = 0; i < 20; ++i) {
				cell = this._getNextCopyPasteCell(hl, cell, bUp, 1, Boolean);
				if (!cell) {
					break;
				}

				params = this._getCellParams(cell);

				if (params) {
					const {ctx, h, ren} = params;

					// do one last check for aggregate store - it's possible different cells have different node types
					if (ren.hasAggregateStore(ctx, h)) {
						urls.push(ctx.getNodeURL());

						// if at least one is valid to paste a value to, then request them all;
						// we could need the aggregate store key for read-only cells if that is where
						// drag-fill starts, and so might as well get all the keys in one shot
						valid ||= ren.canPasteToCell(ctx, cell);
					}
				}
			}

			return valid ? urls : [];
		},

		_getCellParams: function(/*DOMNode*/td) {
			// summary: invoke function on renderer
			var ctx = this.getContext(csi.EMPTY_URL);
			var key = csiHeading.getHeadingNodeMarker(td, ctx);
			if (!key) {
				return null;
			}

			var index = this.getTableCellIndex(td);
			var specs = this.columnData;
			var spec = specs.getByIndex(index, true);
			var h = spec.heading;
			if (h) {
				var ren = csiHeading.getHeadingRendererInContext(ctx, h);
				if (ren) {
					return {
						ctx,
						h,
						indices: specs.getCopyIndices(spec),
						ren: ren.getBaseRenderer(ctx, h),
						spec
					};
				}
			}

			return null;
		},

		/**
		 * Return next cell (up or down) from the given cell that matches the given filter function
		 * @param {number} hl - hierarchy level of row containing cell 
		 * @param {HTMLElement} td - cell
		 * @param {boolean} bUp - direction
		 * @param {number} limit - number of cells to check before giving up (default 100)
		 * @param {(cell: HTMLElement) => boolean} filterFunc - function allowing to skip certain cells (up to limit)
		 * @return {HTMLElement}
		 */
		_getNextCopyPasteCell(hl, td, bUp, limit, filterFunc) {
			const filter = (row, cell) => {
				const qr = this.getQueryResultByDomNode(row);

				if (qr?.bResultNode && !qr.isDivider() && !qr.bHidden && (hl == qr.hl)) {
					return (--limit >= 0) && filterFunc(cell);
				}

				return false;
			}

			return this.getNextCell(td, filter, true, bUp);
		},

		_canCopy: function(/*DOMNode*/td) {
			// summary: check to see if cell can be copied - create context that will
			// be used when calling canPaste so that function can test against
			if (td && this.getQueryResultByDomNode(td).isRoot()) {
				// don't allow drag-fill if root node of table
				return false;
			}

			if (!this.cellSpansMultipleColumns(td)) {
				const p = this._getCellParams(td);
				if (p) {
					const {ctx, h, indices, ren, spec: {ra}} = p;

					// don't allow drag and drop if disabled on attribute - this behavior cannot be overridden by renderer
					if (ra?.isDisableDragEdit) {
						return false;
					}

					// it is possible that the aggregate store key is not yet known and we need to query for them
					const node = ctx.getNode();
					if (node) {
						const key = getCachedAggregateStoreDragFillId(node.$URL, h.m_id);

						if (!ren.restrictsCopy(h) && ren.hasAggregateStore(ctx, h) && isAggregateStoreIdUnknown(key)) {
							this._queryForAggregateStoreDragFillIds(node.$URL, h.m_id, td);
							return false;
						}

						if (ren.canCopy(ctx, h, td)) {
							this._copyParams = p;
							this._copyWidget.set("allowedIndices", indices);
							return true;
						}
					}
				}
			}

			return false;
		},

		_canPaste: function(/*DOMNode*/td) {
			// summary: check to see if copy value can be pasted to cell
			if (!this.cellSpansMultipleColumns(td)) {
				const copyParams = this._copyParams;
				const pasteParams = this._getCellParams(td);

				if (copyParams && pasteParams && (pasteParams.ren === copyParams.ren)) {
					const {ctx, h, ren} = pasteParams;

					if (ren.canPasteToCell(ctx, td) && ren.canPaste(copyParams.ctx, ctx, h)) {
						return true;
					}

					// it is possible that the aggregate store key is not yet known and we need to query for them
					if (ren.hasAggregateStore(ctx, h)) {
						this._resolveAggregateStoreDragFillIds(ctx.getNodeURL(), h.m_id, td);
					}
				}
			}

			return false;
		},

		_copyPaste: function(/*DOMNode*/copyCell, /*NodeList*/pasteList) {
			// summary: copy value from source cell to pasted cells
			const {ctx, h, ren, spec: {ra}} = this._getCellParams(copyCell);
			const hasAggregateStore = ren.hasAggregateStore(ctx, h);
			const sourceUrl = ctx.getNodeURL();

			// DE17709: if copyCell is read-only it does not have an action
			// therefore an action for invoke function is taken
			// from the first element in the pasteList
			const action = html.getElementAction(pasteList[0], true) || html.getDataAttribute(pasteList[0], html.DATA_HEADING);
			const pasteListLength = pasteList.length;

			csiAction.invoke(this, ren.getClickNode(copyCell), action, {
				bHold: false,
				bModal: true,
				bShowWait: true,
				pasteList: pasteList,

				// do not scroll table back to original cell if scrolled away
				nodeToScrollIntoView: pasteListLength ? pasteList[pasteListLength - 1] : null,

				/** return urls for all nodes to change if this attribute is driven by an aggregate store */
				collectAggregateStoreUrlsToProcess(copyValue) {
					const {m_action, pasteList, widget} = this;
					const urls = [];

					pasteList.forEach((td) => {
						widget.getAction(m_action.m_id, ren.getClickNode(td), this);
						this.oldValue = this.getOldValue();
						this.newValue = copyValue;

						if (this._isValueChanged()) {
							urls.push(this._ctx.getNodeURL());
						}
					});

					return urls;
				},

				preProcess() {
					const {m_action, pasteList, widget, m_writer} = this;
					let copyValue = this.getCopyValue();
					const aggregateStoreUrls = hasAggregateStore ? this.collectAggregateStoreUrlsToProcess(copyValue) : [];
					const batchProcess = !!aggregateStoreUrls.length;
					let bChange;

					this.setProgress();

					if (batchProcess) {
						const updateOperation = getCachedAggregateStoreDragFillOperation(sourceUrl, ra.attributeId);
						const type = updateOperation.extraParams.find((param) => param.id === "Type").value;
						const key = this.keyContainer;

						// If the attribute is a map, format the value as a map entry
						if (isContainerValueType(type) && isDefined(key)) {
							copyValue = { [key]: copyValue };
						}

						// need to refresh whole node if there are dependent values in case they are shown as their value or editable status may have changed
						if (updateOperation.operationRequired || ra.getDependents()?.length) {
							this.bRefreshNode = true;
						}

						writeCopyAggregateStoreValue(m_writer, updateOperation, sourceUrl, copyValue, aggregateStoreUrls);
					}

					pasteList.forEach((td) => {
						widget.getAction(m_action.m_id, ren.getClickNode(td), this);
						this.oldValue = this.getOldValue();
						this.newValue = copyValue;

						if (this._isValueChanged()) {
							this.m_urlNode = this._ctx.getNodeURL();
							this.m_formData = null;

							if (batchProcess) {
								// changes have already been sent in UpdateDraggedValue operation above, but still need
								// to make sure any watchAttribute functions are called
								this._notifyOfChange(null, this.getNode(), ra.attributeId);
							} else {
								this.toWriter();
							}

							this._onAction();

							bChange = true;
						}
					});

					if (bChange) {
						widget.onActionPaste(this);
					}

					this.publish();
				},

				postProcessSuccess() {
					// summary: hard to know what postAction would do, so just refresh all rows if specified
					const view = this.widget;

					if (this.m_action.m_postAction) {
						view.bPreserveQueryDisplayCount = true;
						view.redrawData();
					} else if (this.bRefreshNode || view.isComparing) {
						const rows = new Set();

						// get the row merged base to handle cells in row matrices
						this.pasteList.forEach((td) => {
							const row = view.getRowMergeBase(td.parentNode) || td.parentNode;
							rows.add(row);
						});

						// determine row to insert all rows before to improve performance
						view.refreshRowsForDragFill([...rows]);
					} else {
						this.pasteList.forEach(function(td) {
							view.getAction(this.m_action.m_id, this.ren.getClickNode(td), this);
							view.refreshHeadingCell(this._ctx, this.heading, td);
						}, this);
					}
				}
			});
		},

		/**
		 * If drag-fill id for cell is not known, then make request to get them
		 * @param {cnl} nodeUrl 
		 * @param {C8AttributeId} id 
		 * @param {HTMLElement} td 
		 */
		_resolveAggregateStoreDragFillIds(nodeUrl, id, td) {
			const key = getCachedAggregateStoreDragFillId(nodeUrl, id);

			if (isAggregateStoreIdUnknown(key)) {
				this._queryForAggregateStoreDragFillIds(nodeUrl, id, td);
			}
		},

		/**
		 * queries the database for aggregate store drag fill ids
		 * isAggregateStoreIdUnknown should be checked before this method is called
		 * @param {HTMLElement} td 
		 */
		_queryForAggregateStoreDragFillIds(nodeUrl, id, td) {
			const urls = [
				...this._getAdjacentCopyPasteUrls(td, true),
				...this._getAdjacentCopyPasteUrls(td, false)
			];

			if (urls.length) {
				cacheAggregateStoreDragFillIds([nodeUrl, ...urls], id);
			}
		},
	});
});